import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CompanyDetailCard } from '../../../../client/components/CompanyDetailCard';
import { ErrorToast, SuccessToast } from '../../../../../../utils/toasters';
import { CustomAutocomplete } from '../../../../../../shared/components/Autocomplete';
import { AddManagingUser } from './AddManagingUser';
import { ClientService, IClientParams } from '../../../../client/service';
import { Company } from '../../../../../../model/user/Company';
import { useParams } from 'react-router-dom';
import { IParams } from '../../../../../../model/shared/IParams';
import { UserRoles } from '../../../../../../enum/UserRoles';

interface IProps {
    show: boolean;
    onClose: () => void;
    isSupplier?: boolean;
}

const CampaignClientManagementModal = ({ show, onClose, isSupplier }: IProps) => {
    const { id } = useParams<keyof IParams>();
    const [filterParams, setFilterParams] = useState<IClientParams>({
        page: 1,
        perPage: 10,
        type: 'supplier',
        campaignId: id,
        displayName: '',
        excludeCompanies: [],
    });
    const { t } = useTranslation();
    const [isAssigningClients, setIsAssigningClients] = useState(false);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
    const [assignedCompanies, setAssignedCompanies] = useState<Company[]>([]);
    const [updateClients, setUpdateClients] = useState(false);

    useEffect(() => {
        const companiesId: number[] = [];
        selectedCompanies.forEach(item => companiesId.push(item.id));
        setFilterParams(prevState => ({
            ...prevState,
            excludeCompanies: companiesId,
        }));
        ClientService.getClientsOrSuppliers(filterParams).then(response => {
            setCompanies(response.data);
        });
    }, [JSON.stringify(filterParams), updateClients]);

    useEffect(() => {
        ClientService.getAssignedClients(+id!).then(response => {
            setAssignedCompanies(response);
        });
    }, [updateClients]);

    const assignMultipleClientsRequest = () => {
        if (isAssigningClients) return;
        setIsAssigningClients(true);
        const brandMasterAccounts: any = [];
        selectedCompanies.forEach(company => {
            brandMasterAccounts.push(company?.companyUsers?.find(item => item.userType === UserRoles.BRAND_MASTER));
        });
        ClientService.assignMultipleClients(+id!, brandMasterAccounts)
            .then(() => {
                SuccessToast(t('brand.campaign.dashboard.modal.successfully_assigned_clients'));
                setUpdateClients(prevState => !prevState);
                setSelectedCompanies([]);
            })
            .catch(err => ErrorToast(err))
            .finally(() => {
                setIsAssigningClients(false);
            });
    };
    const changeHandler = (paramKey: string, paramValue: number | string | string[]) => {
        const allCampaigns: Company[] = companies;
        const selectedItem = companies?.find(company => company.id === Number(paramValue));
        selectedItem && selectedCompanies.push(selectedItem);
        const deleteSelectedCompany = allCampaigns.filter(item => item.id !== selectedItem?.id);
        setCompanies(deleteSelectedCompany);
    };

    const removeUser = (id: number) => {
        const removeSelectedCompany = selectedCompanies.filter(item => item.id !== id);
        setSelectedCompanies(removeSelectedCompany);
        const addRemovedCompany = selectedCompanies.find(item => item.id === id);
        addRemovedCompany && companies.push(addRemovedCompany);
    };

    return (
        <Modal show={show} onHide={onClose} size={'lg'} centered>
            <Modal.Header closeButton className="border-bottom border-light">
                <Modal.Title>
                    {isSupplier ? (
                        <div>
                            <h4 className="mb-0">{t('brand.campaign.dashboard.modal.client_team')}</h4>
                            <span className="text-muted">{t('brand.campaign.dashboard.modal.client_team_desc')}</span>
                        </div>
                    ) : (
                        <div>
                            <h4 className="mb-0">{t('brand.campaign.dashboard.modal.agency_team')}</h4>
                            <span className="text-muted">{t('brand.campaign.dashboard.modal.agency_team_desc')}</span>
                        </div>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isSupplier && (
                    <>
                        <h6 className="mb-3"> {t('brand.campaign.dashboard.modal.client_team_share')}</h6>
                        <CustomAutocomplete
                            dataArray={companies ?? []}
                            isWithImage={false}
                            inputLabel="displayName"
                            key={selectedCompanies.map(c => c.id)?.join(',')}
                            isMultiple={false}
                            queryFilterKey="displayName"
                            changeEventKey="displayName"
                            setFilterState={setFilterParams}
                            inputLabelTranslation={t('brand.campaign.dashboard.modal.your_clients')}
                            handleChangeParams={changeHandler}
                        />
                        {selectedCompanies.map(item => (
                            <AddManagingUser
                                key={item?.id}
                                removeUser={removeUser}
                                company={item}
                                companyMaster={item.ownedBy}
                            />
                        ))}
                        <div className="w-100 text-end my-2">
                            <Button
                                onClick={assignMultipleClientsRequest}
                                disabled={!selectedCompanies.length || isAssigningClients}
                                variant="primary"
                            >
                                {t('general.button.share')}
                            </Button>
                        </div>
                    </>
                )}
                <div className="overflow-auto max-height-40vh pr-3">
                    {assignedCompanies?.map(item => (
                        <CompanyDetailCard
                            key={item.id}
                            company={item}
                            displayUsers={true}
                            companyMaster={item.ownedBy}
                        />
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={onClose}>
                    {t('general.button.cancel')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CampaignClientManagementModal;
